import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './report.scss';
import {
    getReportStatusConfig,
    getDisplayTitle,
    shouldLoadTitle,
    loadReportTitle,
    checkUserStoppedState,
    setUserStoppedState,
    validateEventTarget,
    initializeReportTitle,
    checkReportCompletionStatus
} from '../../hooks/DeerFlow/useReportUtils';
import ReportCardAIIdentification from '../../../assets/images/web/ReportCardAIIdentification.webp';

// 全局初始化标记，确保每个uniqueReportId只初始化一次
const globalInitializedReports = new Set();

// 移除内联样式常量，使用 CSS 类

/**
 * 简单的报告通知卡片组件，显示在聊天界面中
 */
const SimpleReportNotice = ({
    threadId,
    reportId,
    uniqueReportId = null,
    title = '研究报告',
    initiallyOpened = false,
    onSidebarOpen,
    onSidebarClose,
    stateUpdaters = {},
    activeReportCardId = null,
    status = 'loading',
    description = '北京大学研究生就业研究',
    themeColor = 1
}) => {
    // 使用uniqueReportId或reportId作为卡片ID
    const cardId = uniqueReportId || reportId;

    // 永久保护机制：检查localStorage中是否有停止标记
    const wasStopped = checkUserStoppedState(cardId);

    // 🔥 新增：检查报告的真实完成状态
    const { isCompleted, realStatus } = checkReportCompletionStatus(cardId);

    // 🔥 修复：使用真实状态初始化，而不是传入的status
    const initialStatus = wasStopped ? 'stopped' : realStatus;

    const [isOpen, setIsOpen] = useState(initiallyOpened);
    const [localDescription, setLocalDescription] = useState(description);
    const [localStatus, setLocalStatus] = useState(initialStatus);
    const userStoppedRef = useRef(wasStopped); // 标记是否用户手动停止
    const titleUpdateTimeoutRef = useRef(null); // 标题更新防抖
    const lastLoadedTitleRef = useRef(''); // 缓存最后加载的标题，避免重复更新
    const hasLoadedFromStorageRef = useRef(false); // 标记是否已从localStorage加载过

    const [localTitle, setLocalTitle] = useState(() => {
        // 🔥 修复：传入真实状态而不是props中的status
        return initializeReportTitle(title, initialStatus, cardId, wasStopped);
    });

    // 初始化时缓存真实标题
    useEffect(() => {
        // 🔥 修复：使用计算出的真实状态
        const effectiveStatus = wasStopped ? 'stopped' : realStatus;

        // 如果传入了真实标题，缓存它
        if (effectiveStatus === 'success' && title && title !== '研究报告') {
            lastLoadedTitleRef.current = title;
        }
        // 如果当前localTitle不是默认标题，也缓存它
        else if (localTitle && localTitle !== '研究报告') {
            lastLoadedTitleRef.current = localTitle;
        }
    }, []); // 只在组件挂载时执行一次

    // 获取状态配置
    const statusData = useMemo(() => {
        const config = getReportStatusConfig(themeColor);
        return config[localStatus] || config.loading;
    }, [localStatus, themeColor]);

    // 从localStorage加载报告标题 - 只在刚变为success状态时加载一次
    useEffect(() => {
        if (shouldLoadTitle(localStatus, cardId, hasLoadedFromStorageRef.current, lastLoadedTitleRef.current, localTitle)) {
            // 检查是否已经有其他相同cardId的组件实例加载过
            const alreadyInitialized = globalInitializedReports.has(cardId);

            hasLoadedFromStorageRef.current = true; // 标记已尝试加载

            const { title: savedTitle, success } = loadReportTitle(cardId);

            if (success && savedTitle) {
                lastLoadedTitleRef.current = savedTitle;
                setLocalTitle(savedTitle);
            }

            // 标记为已初始化，避免重复尝试
            if (!alreadyInitialized) {
                globalInitializedReports.add(cardId);
            }
        }
    }, [cardId, localStatus, localTitle]);

    // 监听从父组件传入的status变化，但要考虑保护机制
    useEffect(() => {
        // 强化保护：如果有用户停止保护标志，完全拒绝任何父组件的status变化
        if (userStoppedRef.current) {
            return;
        }
        // 如果当前是stopped状态，拒绝任何父组件的status变化
        if (localStatus === 'stopped') {
            return;
        }

        // 🔥 修复：如果已经确定了真实状态，减少对传入status的依赖
        // 只有在真实状态检查失败时才使用传入的status
        const { realStatus: currentRealStatus } = checkReportCompletionStatus(cardId);
        if (currentRealStatus && currentRealStatus !== 'loading') {
            // 如果能获取到真实状态且不是loading，优先使用真实状态
            if (currentRealStatus !== localStatus) {
                setLocalStatus(currentRealStatus);
            }
            return;
        }

        // 只有在状态从success变回loading时才需要保护（这种情况下可能是错误的状态回退）
        if (localStatus === 'success' && status === 'loading' && localTitle && localTitle !== '研究报告') {
            return;
        }

        // 只在状态真正变化时才更新
        if (status !== localStatus) {
            setLocalStatus(status);

            // 重要修复：不再自动重置标题！
            // 已完成的报告应该保持其真实标题，不应该因为状态变化而重置
            // 删除了原来的标题重置逻辑，让已完成的报告保持稳定
        }
    }, [status, localStatus, localTitle, cardId]);

    // 🎯 新增：监听从父组件传入的title变化
    useEffect(() => {
        // 只有当父组件传入了有效的真实标题时才更新
        if (title && title !== '研究报告' && title !== localTitle) {
            setLocalTitle(title);
            lastLoadedTitleRef.current = title;
        }
    }, [title, localTitle, cardId]);

    // 统一处理事件更新
    const handleEventUpdate = useCallback(
        (detail) => {
            if (!validateEventTarget(detail, cardId)) {
                return;
            }

            // 额外保护：如果当前是已完成的报告且有真实标题，只拒绝重复的状态变更，但允许合理的状态转换
            if (localStatus === 'success' && localTitle && localTitle !== '研究报告') {
                // 只拒绝重复的success状态更新，但允许其他状态变更（如用户停止）
                if (detail.status === 'success' && detail.status === localStatus) {
                    // 这是重复的success状态更新，只处理标题更新，跳过状态更新
                    if (detail.title && detail.title !== localTitle && detail.title !== '研究报告' && detail.title.trim().length > 0) {
                        // 允许标题更新
                        if (titleUpdateTimeoutRef.current) {
                            clearTimeout(titleUpdateTimeoutRef.current);
                        }
                        titleUpdateTimeoutRef.current = setTimeout(() => {
                            lastLoadedTitleRef.current = detail.title;
                            setLocalTitle(detail.title);
                            titleUpdateTimeoutRef.current = null;
                        }, 100);
                    }
                    return; // 跳过重复的状态更新
                }
                // 对于其他状态变更（如用户停止），继续处理
            }

            // 强化保护机制：如果有用户停止保护标志，则完全拒绝任何非用户停止的状态变更
            if (userStoppedRef.current && (!detail.userStopped || detail.status !== 'stopped')) {
                return;
            }

            // 如果当前状态是stopped，则只接受用户停止的事件
            if (localStatus === 'stopped' && (!detail.userStopped || detail.status !== 'stopped')) {
                return;
            }

            // 只在没有保护标志且确实需要更新标题时才更新
            if (detail.title && !userStoppedRef.current) {
                // 检查标题是否真的不同且不是默认标题，避免不必要的更新
                if (
                    detail.title !== lastLoadedTitleRef.current &&
                    detail.title !== localTitle &&
                    detail.title !== '研究报告' &&
                    detail.title.trim().length > 0
                ) {
                    // 清除之前的定时器
                    if (titleUpdateTimeoutRef.current) {
                        clearTimeout(titleUpdateTimeoutRef.current);
                    }

                    // 设置新的防抖定时器
                    titleUpdateTimeoutRef.current = setTimeout(() => {
                        lastLoadedTitleRef.current = detail.title;
                        setLocalTitle(detail.title);
                        titleUpdateTimeoutRef.current = null;
                    }, 100); // 100ms防抖
                }
            }

            if (detail.status) {
                setLocalStatus(detail.status);

                // 如果是用户手动停止，设置永久保护标志
                if (detail.status === 'stopped' && detail.userStopped) {
                    userStoppedRef.current = true;
                    setUserStoppedState(cardId);
                    // 不再设置定时器清除保护，永久保护直到页面刷新或组件卸载
                }
            }

            if (detail.description) {
                setLocalDescription(detail.description);
            }

            // 只在特定条件下更新isOpen
            // 注意：不要在这里直接设置isOpen，应该让activeReportCardId状态管理来统一控制
            // 只有在明确要求关闭时才直接设置
            if (detail.isOpen === false) {
                setIsOpen(false);
                if (onSidebarClose) {
                    onSidebarClose();
                }
            }
            // 对于打开操作，依赖activeReportCardId的useEffect来处理，确保同时只有一个报告打开
        },
        [cardId, activeReportCardId, onSidebarClose]
    );

    // 处理activeReportCardId变化
    useEffect(() => {
        // 当activeReportCardId变化时，更新当前卡片的打开状态
        // 如果activeReportCardId为null，所有卡片都应该关闭
        // 如果activeReportCardId匹配当前卡片ID，则打开，否则关闭
        setIsOpen(activeReportCardId === cardId);
    }, [activeReportCardId, cardId]);

    // 响应initiallyOpened属性变化
    useEffect(() => {
        if (initiallyOpened) {
            // 如果有activeReportCardId状态管理，遵循它的控制
            if (activeReportCardId !== null) {
                setIsOpen(activeReportCardId === cardId);
            } else {
                // 如果没有activeReportCardId管理，则需要通知父组件打开此报告
                // 这样可以确保父组件设置正确的activeReportCardId，从而关闭其他报告
                if (onSidebarOpen) {
                    onSidebarOpen(threadId, reportId, uniqueReportId);
                } else {
                    // 如果没有父组件回调，才直接设置（向后兼容）
                    setIsOpen(true);
                }
            }
        }
    }, [initiallyOpened, cardId, activeReportCardId, onSidebarOpen, threadId, reportId, uniqueReportId]);

    // 监听报告内容更新事件
    useEffect(() => {
        function handleReportContentUpdate(event) {
            if (event.detail?.uniqueReportId === cardId) {
                handleEventUpdate(event.detail);
            }
        }
        window.addEventListener('report-content-update', handleReportContentUpdate);
        return () => window.removeEventListener('report-content-update', handleReportContentUpdate);
    }, [cardId, handleEventUpdate]);

    // 持久化监听：无论侧边栏是否打开，都把报告的进行中/完成状态写入 unified 本地存储
    useEffect(() => {
        function persistReportUpdateToStorage(event) {
            const detail = event.detail || {};
            const id = detail.uniqueReportId || detail.reportId || cardId;
            if (!id) return;
            const isActivityEvent = event.type === 'activity-content-update';

            try {
                // helper: 从markdown内容提取第一个一级标题作为标题
                const extractTitleFromMarkdown = (text) => {
                    if (!text || typeof text !== 'string') return null;
                    const match = text.match(/^#\s*(.+)/m);
                    return match && match[1] ? match[1].trim() : null;
                };
                const unifiedKey = 'deerflow_reports_data';
                const unifiedData = JSON.parse(localStorage.getItem(unifiedKey) || '{"completed":{},"generating":{}}');
                if (!unifiedData.completed) unifiedData.completed = {};
                if (!unifiedData.generating) unifiedData.generating = {};
                // 区分事件来源：activity-content-update 的 content 应视为 activityContent
                const incomingActivity = isActivityEvent
                    ? detail.content || detail.activityData || detail.activityContent || ''
                    : detail.activityData || detail.activityContent || '';
                const incomingReport = !isActivityEvent
                    ? detail.content || detail.reportData || detail.reportContent || ''
                    : detail.reportData || detail.reportContent || '';
                const isFinal = !!detail.isFinal || detail.status === 'success' || detail.status === 'completed';

                // 合并已有的进行中数据，避免覆盖之前的思考过程
                const existingGen = unifiedData.generating[id] || {};
                const mergedActivity = (() => {
                    const prev = existingGen.activityContent || '';
                    if (!prev) return incomingActivity || prev;
                    if (!incomingActivity) return prev;
                    // 避免重复追加：如果 incoming 活动已经包含在 prev 中，保留 prev
                    if (prev.includes(incomingActivity) || incomingActivity.includes(prev))
                        return prev.length >= incomingActivity.length ? prev : incomingActivity;
                    return prev + '\n\n' + incomingActivity;
                })();
                const mergedReport = (() => {
                    const prev = existingGen.reportContent || '';
                    if (!prev) return incomingReport || prev;
                    if (!incomingReport) return prev;
                    if (prev.includes(incomingReport) || incomingReport.includes(prev))
                        return prev.length >= incomingReport.length ? prev : incomingReport;
                    return prev + '\n\n' + incomingReport;
                })();

                if (isFinal) {
                    // 如果没有 incoming activity，尝试从 generating 中获取累积的思考过程
                    const finalActivity = incomingActivity || existingGen.activityContent || '';
                    const finalReport = incomingReport || existingGen.reportContent || '';
                    const finalTitle = detail.title || extractTitleFromMarkdown(finalReport) || existingGen.title || '研究报告';

                    unifiedData.completed[id] = {
                        reportId: id,
                        uniqueReportId: id,
                        threadId: detail.threadId || threadId || null,
                        title: finalTitle,
                        activityContent: finalActivity,
                        reportContent: finalReport,
                        timestamp: detail.timestamp || Date.now(),
                        isFinal: true,
                        savedAt: Date.now()
                    };
                    if (unifiedData.generating[id]) delete unifiedData.generating[id];
                } else {
                    // 保存或合并进行中数据
                    const gen = {
                        reportId: id,
                        uniqueReportId: id,
                        threadId: detail.threadId || threadId || null,
                        title: detail.title || extractTitleFromMarkdown(mergedReport) || existingGen.title || '研究报告',
                        activityContent: mergedActivity,
                        reportContent: mergedReport,
                        timestamp: detail.timestamp || Date.now(),
                        isFinal: false,
                        savedAt: Date.now()
                    };
                    unifiedData.generating[id] = gen;
                }

                localStorage.setItem(unifiedKey, JSON.stringify(unifiedData));
            } catch (e) {
                console.warn('保存 report-content-update 到 localStorage 失败:', e);
            }
        }

        window.addEventListener('report-content-update', persistReportUpdateToStorage);
        window.addEventListener('activity-content-update', persistReportUpdateToStorage);

        return () => {
            window.removeEventListener('report-content-update', persistReportUpdateToStorage);
            window.removeEventListener('activity-content-update', persistReportUpdateToStorage);
        };
    }, [cardId, threadId]);

    // 监听报告状态变化事件
    useEffect(() => {
        function handleReportStateChange(event) {
            // 支持多种ID匹配方式：reportId, uniqueReportId, 或cardId
            const eventReportId = event.detail?.reportId;
            const eventUniqueReportId = event.detail?.uniqueReportId;

            if (
                eventReportId === reportId ||
                eventReportId === uniqueReportId ||
                eventReportId === cardId ||
                eventUniqueReportId === reportId ||
                eventUniqueReportId === uniqueReportId ||
                eventUniqueReportId === cardId
            ) {
                handleEventUpdate(event.detail);
            }
        }
        window.addEventListener('report-state-change', handleReportStateChange);
        return () => window.removeEventListener('report-state-change', handleReportStateChange);
    }, [reportId, uniqueReportId, cardId, handleEventUpdate]);

    // 🎯 新增：监听会话切换事件，立即更新标题和状态
    useEffect(() => {
        function handleSessionSwitched(event) {
            // 会话切换时，重新检查当前卡片的状态和标题
            if (cardId) {
                const { isCompleted, realStatus } = checkReportCompletionStatus(cardId);

                // 更新状态
                if (realStatus !== localStatus && !userStoppedRef.current) {
                    setLocalStatus(realStatus);
                }

                // 从新的本地存储重新加载标题
                if (realStatus === 'success') {
                    try {
                        const unifiedData = JSON.parse(localStorage.getItem('deerflow_reports_data') || '{"completed":{},"generating":{}}');
                        const completedReports = unifiedData.completed || {};
                        const reportData = completedReports[cardId];

                        if (reportData && reportData.title && reportData.title !== '研究报告') {
                            // 只有在标题确实不同时才更新
                            if (reportData.title !== localTitle) {
                                setLocalTitle(reportData.title);
                                lastLoadedTitleRef.current = reportData.title;
                            }
                        }
                    } catch (e) {
                        console.error('会话切换时加载报告标题失败:', e);
                    }
                }
            }
        }

        window.addEventListener('session-switched', handleSessionSwitched);
        return () => window.removeEventListener('session-switched', handleSessionSwitched);
    }, [cardId, localStatus, localTitle]);

    // 🔥 新增：监听强制打开报告卡片事件
    useEffect(() => {
        function handleForceOpenReportCard(event) {
            const { reportId: eventReportId, uniqueReportId: eventUniqueReportId, reason } = event.detail;

            // 检查事件是否针对当前卡片
            if (
                eventReportId === cardId ||
                eventUniqueReportId === cardId ||
                eventReportId === reportId ||
                eventReportId === uniqueReportId ||
                eventUniqueReportId === reportId ||
                eventUniqueReportId === uniqueReportId
            ) {
                setIsOpen(true);

                // 如果有侧边栏打开回调，也调用它
                if (onSidebarOpen && typeof onSidebarOpen === 'function') {
                    onSidebarOpen(threadId, reportId, uniqueReportId);
                }
            }
        }

        window.addEventListener('force-open-report-card', handleForceOpenReportCard);
        return () => window.removeEventListener('force-open-report-card', handleForceOpenReportCard);
    }, [cardId, reportId, uniqueReportId, threadId, onSidebarOpen]);

    // 让父组件可以更新状态
    useEffect(() => {
        if (stateUpdaters) {
            // 🔥 关键修复：同时注册reportId和uniqueReportId的状态更新器
            const keysToRegister = [];
            if (reportId) keysToRegister.push(`setIsOpen_${reportId}`);
            if (uniqueReportId && uniqueReportId !== reportId) keysToRegister.push(`setIsOpen_${uniqueReportId}`);

            keysToRegister.forEach((key) => {
                stateUpdaters[key] = setIsOpen;
            });

            return () => {
                keysToRegister.forEach((key) => {
                    stateUpdaters[key] = null;
                });
            };
        }
    }, [stateUpdaters, reportId, uniqueReportId, cardId]);

    // 组件卸载时清理定时器和全局标记
    useEffect(() => {
        return () => {
            if (titleUpdateTimeoutRef.current) {
                clearTimeout(titleUpdateTimeoutRef.current);
            }
            // 清理全局标记（可选，因为页面刷新时会自动清理）
            // globalInitializedReports.delete(cardId);
        };
    }, []);

    // 处理点击事件 - 只用于"查看"/"收起"文字
    const handleToggleOpen = useCallback(
        (e) => {
            e.stopPropagation();
            e.preventDefault();

            // 如果是stopped状态，不允许操作
            if (localStatus === 'stopped') {
                return;
            }

            // 🔥 新增：保存当前选中的报告ID到localStorage（对象形式，支持多个报告）
            const selectedReportId = uniqueReportId || reportId;
            if (selectedReportId && threadId) {
                try {
                    const selectedReports = JSON.parse(localStorage.getItem('selected_report_id') || '{}');
                    selectedReports[threadId] = selectedReportId;
                    localStorage.setItem('selected_report_id', JSON.stringify(selectedReports));
                } catch (error) {
                    console.warn('保存选中报告ID失败:', error);
                }
            }

            // 如果当前报告已经打开且是活跃卡片，则关闭它
            if (isOpen && activeReportCardId === cardId) {
                // 🔥 新增：关闭时清理保存的报告ID（对象形式，只清理当前threadId）
                if (threadId) {
                    try {
                        const selectedReports = JSON.parse(localStorage.getItem('selected_report_id') || '{}');
                        delete selectedReports[threadId];
                        if (Object.keys(selectedReports).length === 0) {
                            localStorage.removeItem('selected_report_id');
                        } else {
                            localStorage.setItem('selected_report_id', JSON.stringify(selectedReports));
                        }
                    } catch (error) {
                        console.warn('清理保存的报告ID失败:', error);
                    }
                }

                // 不直接设置本地状态，让父组件的状态管理来处理
                onSidebarClose?.(reportId);
                return;
            }

            // 如果要打开报告（无论当前状态如何），都调用打开函数
            // 让父组件的activeReportCardId状态管理来统一控制
            onSidebarOpen?.(threadId, reportId, uniqueReportId);
        },
        [isOpen, activeReportCardId, cardId, onSidebarClose, onSidebarOpen, reportId, threadId, uniqueReportId, localStatus]
    );

    // 渲染卡片内容
    const renderCardContent = () => {
        // 所有状态共用的顶部消息
        const topMessage = <div className="top-message">我这就开始。研究完成后，我会告诉你。在此期间，你可以离开这个对话</div>;

        const displayTitle = getDisplayTitle(localTitle);

        // 状态为stopped或error时的布局（不显示查看按钮）
        if (localStatus === 'stopped' || localStatus === 'error') {
            return (
                <div className="content-container">
                    {topMessage}
                    <div className="status-row">
                        <div className="status-left">
                            {statusData.icon()}
                            <div className={`status-label ${statusData.className}`}>{statusData.text}</div>
                        </div>

                        {/* stopped和error状态下不显示查看按钮 */}
                    </div>

                    {/* 停止和错误状态显示"研究报告" */}
                    <div className="description">{displayTitle}</div>
                </div>
            );
        }

        // loading和success状态共用的布局（显示查看按钮）
        return (
            <div className="content-container">
                {topMessage}
                <div className="status-row">
                    <div className="status-left">
                        {statusData.icon()}
                        <div className={`status-label ${statusData.className}`}>{statusData.text}</div>
                        {/* AI标识 - 只在已完成状态显示 */}
                        {localStatus === 'success' && (
                            <div className="ai-identification">
                                <img src={ReportCardAIIdentification} alt="AI生成" className="ai-badge" />
                            </div>
                        )}
                    </div>
                    <div className="view-toggle" onClick={handleToggleOpen}>
                        <span>{isOpen ? '收起' : '查看'}</span>
                        <span className="arrow-icon">→</span>
                    </div>
                </div>

                {/* 根据状态显示不同标题：加载中显示"研究报告"，已完成显示真实标题 */}
                <div className="description">{displayTitle}</div>
            </div>
        );
    };

    return (
        <div
            className="simple-report-notice card"
            data-type="report-notice"
            data-title={localTitle}
            data-thread-id={threadId}
            data-report-id={uniqueReportId || reportId}
            data-unique-report-id={uniqueReportId || reportId}
            data-state={isOpen ? 'opened' : 'closed'}
            data-status={localStatus}
        >
            {renderCardContent()}
        </div>
    );
};

export default React.memo(SimpleReportNotice);
